import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Key, Loader2, Check, X, User, Clock, Shield } from 'lucide-react';

const PasswordResetRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [newPasswords, setNewPasswords] = useState({});
  const [showPasswordInputs, setShowPasswordInputs] = useState({});

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.getPasswordResetRequests();
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch password reset requests');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (requestId) => {
    const password = newPasswords[requestId];
    if (!password || password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setProcessingId(requestId);
    try {
      await api.resetUserPassword(requestId, password);
      toast.success('Password reset successfully');
      setNewPasswords({ ...newPasswords, [requestId]: '' });
      setShowPasswordInputs({ ...showPasswordInputs, [requestId]: false });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('Are you sure you want to reject this password reset request?')) {
      return;
    }

    setProcessingId(requestId);
    try {
      await api.rejectPasswordReset(requestId);
      toast.success('Password reset request rejected');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessingId(null);
    }
  };

  const getModuleLabel = (module) => {
    switch (module) {
      case 'sales_calling': return 'Sales Calling';
      case 'complaints': return 'Complaints';
      case 'class_delivery': return 'Class Delivery';
      default: return module;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'caller': return 'Sales Caller';
      case 'complaint_entry': return 'Complaint Entry';
      case 'complaint_solver': return 'Complaint Solver';
      case 'class_conduction_agent': return 'Class Conduction Agent';
      case 'teacher': return 'Teacher';
      default: return role;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="password-reset-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-headings text-slate-900 flex items-center gap-3">
            <Key size={28} className="text-slate-700" />
            Password Reset Requests
          </h1>
          <p className="text-slate-600 font-body mt-1">
            Review and handle password reset requests from agents
          </p>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Shield size={64} className="mx-auto text-slate-200 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Pending Requests</h3>
          <p className="text-slate-500">There are no password reset requests at this time.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request.id}
              className="bg-white rounded-xl border border-slate-200 shadow-sm p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <User size={24} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900">{request.user_name}</h3>
                    <p className="text-slate-600 text-sm">{request.user_email}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="px-2 py-1 bg-slate-100 rounded text-xs font-medium text-slate-600">
                        {getRoleLabel(request.user_role)}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 rounded text-xs font-medium text-blue-700">
                        {getModuleLabel(request.user_module)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                      <Clock size={14} />
                      <span>Requested: {formatDate(request.created_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-3">
                  {showPasswordInputs[request.id] ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="password"
                        placeholder="New password (min 6 chars)"
                        value={newPasswords[request.id] || ''}
                        onChange={(e) => setNewPasswords({ ...newPasswords, [request.id]: e.target.value })}
                        className="px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 w-48"
                      />
                      <button
                        onClick={() => handleResetPassword(request.id)}
                        disabled={processingId === request.id}
                        className="p-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {processingId === request.id ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Check size={18} />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowPasswordInputs({ ...showPasswordInputs, [request.id]: false });
                          setNewPasswords({ ...newPasswords, [request.id]: '' });
                        }}
                        className="p-2 bg-slate-200 text-slate-600 rounded-md hover:bg-slate-300"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowPasswordInputs({ ...showPasswordInputs, [request.id]: true })}
                        className="px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 text-sm font-medium flex items-center gap-2"
                      >
                        <Key size={16} />
                        Set New Password
                      </button>
                      <button
                        onClick={() => handleReject(request.id)}
                        disabled={processingId === request.id}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm font-medium disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordResetRequests;
